import React, { useState, useEffect, useRef } from 'react';
import { useB2BReservations } from '../../hooks/useB2BReservations';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Package, Calendar, ChevronDown, ChevronUp, RefreshCw, Clock, Download } from 'lucide-react';
import { Button } from '../ui/Button';
import {
  generateReservationHTML,
  htmlToPdfBase64,
  type SupplierProfile,
} from '../../lib/pdfGenerator';

interface ProductInfo {
  id: string;
  name: string;
  sku?: string;
  color_id?: string;
  size_id?: string;
}

const SUPPLIER_FIELDS = 'company_name, legal_entity_name, company_street, company_house_number, company_postal_code, company_city, company_country, company_address, company_phone, company_email, company_website, kvk_number, vat_number, company_tax_id, iban, bic, bank_name, logo_url, payment_terms_days';

function downloadPdfBase64(base64: string, filename: string) {
  const byteChars = atob(base64);
  const byteArray = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
  const blob = new Blob([byteArray], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const B2BReservationHistory: React.FC = () => {
  const { reservations, loading, refreshReservations } = useB2BReservations();
  const { buyer } = useAuth();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [products, setProducts] = useState<Record<string, ProductInfo>>({});
  const [colors, setColors] = useState<Record<string, string>>({});
  const [sizes, setSizes] = useState<Record<string, string>>({});
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const supplierRef = useRef<SupplierProfile | null>(null);

  const getSupplier = async (): Promise<SupplierProfile | null> => {
    if (supplierRef.current) return supplierRef.current;
    if (!buyer) return null;
    const { data } = await supabase
      .from('users')
      .select(SUPPLIER_FIELDS)
      .eq('id', buyer.admin_id)
      .single();
    if (!data) return null;
    supplierRef.current = data as SupplierProfile;
    return supplierRef.current;
  };

  const handleDownloadReservation = async (reservation: any) => {
    if (!buyer) return;
    setDownloadingId(reservation.id);
    try {
      const supplier = await getSupplier();
      if (!supplier) throw new Error('Supplier profile unavailable');

      const items = (reservation.reservation_items || []).map((item: any) => {
        const p = products[item.product_id];
        return {
          name: p?.name || 'Product',
          sku: p?.sku,
          color_name: p?.color_id ? colors[p.color_id] : undefined,
          size_name: p?.size_id ? sizes[p.size_id] : undefined,
          quantity: item.quantity,
          unit_price: parseFloat(item.unit_price),
          total_price: parseFloat(item.total_price),
          product_id: item.product_id,
        };
      });

      const html = generateReservationHTML(reservation, buyer, items, supplier);
      const pdfBase64 = await htmlToPdfBase64(html);
      downloadPdfBase64(pdfBase64, `Reservation-${reservation.reservation_number}.pdf`);
    } catch (err) {
      console.error('Failed to download reservation PDF:', err);
      alert('Failed to generate reservation PDF.');
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      if (!buyer) return;
      const productIds = new Set<string>();
      reservations.forEach(r => r.reservation_items?.forEach(item => {
        if (item.product_id) productIds.add(item.product_id);
      }));
      if (productIds.size === 0) return;

      const [{ data }, { data: colorData }, { data: sizeData }] = await Promise.all([
        supabase.from('products').select('id, name, sku, color_id, size_id').in('id', Array.from(productIds)),
        supabase.from('product_colors').select('id, name').eq('admin_id', buyer.admin_id),
        supabase.from('product_sizes').select('id, name').eq('admin_id', buyer.admin_id),
      ]);

      if (data) {
        const map: Record<string, ProductInfo> = {};
        data.forEach(p => { map[p.id] = p; });
        setProducts(map);
      }
      if (colorData) {
        const map: Record<string, string> = {};
        colorData.forEach(c => { map[c.id] = c.name; });
        setColors(map);
      }
      if (sizeData) {
        const map: Record<string, string> = {};
        sizeData.forEach(s => { map[s.id] = s.name; });
        setSizes(map);
      }
    };
    fetchProducts();
  }, [reservations, buyer]);

  const statusColors: Record<string, string> = {
    reserved: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    ready: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    fulfilled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    expired: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  const formatCurrency = (n: number) => new Intl.NumberFormat('en-EU', { style: 'currency', currency: buyer?.preferred_currency || 'EUR' }).format(n);

  if (loading && reservations.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reservation History</h3>
        <Button variant="secondary" size="sm" onClick={refreshReservations}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {reservations.length === 0 ? (
        <div className="card p-12 text-center">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">No reservations yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reservations.map(r => (
            <div key={r.id} className="card overflow-hidden">
              <button
                onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                className="w-full px-4 py-3.5 flex items-start sm:items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors gap-3"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 min-w-0">
                  <span className="font-mono text-sm font-bold text-gray-800 dark:text-gray-200">{r.reservation_number}</span>
                  <span className={`self-start sm:self-auto px-2.5 py-0.5 text-xs font-semibold rounded-full capitalize ${statusColors[r.status] || ''}`}>
                    {r.status}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 sm:hidden">
                    <Clock className="h-3 w-3" /> {formatDate(r.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="hidden sm:flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                    <Calendar className="h-3.5 w-3.5" /> {formatDate(r.created_at)}
                  </span>
                  <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{formatCurrency(r.total_amount)}</span>
                  {expandedId === r.id ? <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                </div>
              </button>
              {expandedId === r.id && (
                <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700">
                  {(r as any).pickup_scheduled_date && (
                    <div className="flex items-center gap-2 mt-3 mb-3 text-sm text-gray-600 dark:text-gray-400">
                      <Calendar className="h-4 w-4 flex-shrink-0" />
                      <span>Pickup: <span className="font-semibold text-gray-800 dark:text-gray-200">{formatDate((r as any).pickup_scheduled_date)}</span></span>
                    </div>
                  )}

                  {/* Desktop table */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          <th className="pb-2 font-semibold">Item</th>
                          <th className="pb-2 text-center font-semibold">Qty</th>
                          <th className="pb-2 text-right font-semibold">Unit Price</th>
                          <th className="pb-2 text-right font-semibold">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {r.reservation_items?.map(item => {
                          const p = products[item.product_id];
                          const variant = [p?.color_id ? colors[p.color_id] : '', p?.size_id ? sizes[p.size_id] : ''].filter(Boolean).join(' / ');
                          return (
                            <tr key={item.id}>
                              <td className="py-2.5 text-gray-800 dark:text-gray-200">
                                <span className="font-medium">{p?.name || 'Product'}</span>
                                {p?.sku && <span className="text-gray-400 dark:text-gray-500 ml-1 font-mono text-xs">({p.sku})</span>}
                                {variant && <span className="block text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">{variant}</span>}
                              </td>
                              <td className="py-2.5 text-center font-semibold">{item.quantity}</td>
                              <td className="py-2.5 text-right text-gray-600 dark:text-gray-400">{formatCurrency(item.unit_price)}</td>
                              <td className="py-2.5 text-right font-bold text-gray-900 dark:text-gray-100">{formatCurrency(item.total_price)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile card list */}
                  <div className="sm:hidden space-y-2 mt-1">
                    {r.reservation_items?.map(item => {
                      const p = products[item.product_id];
                      const variant = [p?.color_id ? colors[p.color_id] : '', p?.size_id ? sizes[p.size_id] : ''].filter(Boolean).join(' / ');
                      return (
                        <div key={item.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                              {p?.name || 'Product'}
                            </p>
                            {p?.sku && <p className="text-xs font-mono text-gray-400 dark:text-gray-500">{p.sku}</p>}
                            {variant && <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">{variant}</p>}
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formatCurrency(item.unit_price)} each</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-gray-500 dark:text-gray-400">×{item.quantity}</p>
                            <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mt-0.5">{formatCurrency(item.total_price)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Totals row */}
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex items-center justify-between sm:justify-start gap-3">
                      <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">Order Total</span>
                      <span className="text-base font-bold text-gray-900 dark:text-gray-100">{formatCurrency(r.total_amount)}</span>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleDownloadReservation(r)}
                      disabled={downloadingId === r.id}
                      className="w-full sm:w-auto"
                    >
                      <Download className="h-4 w-4 mr-1.5" />
                      {downloadingId === r.id ? 'Generating...' : 'Download PDF'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
